import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';

import { trackEvent } from 'helpers';

export type ButtonVariant =
  | 'primary'
  | 'primaryInverse'
  | 'primaryLight'
  | 'primaryBright'
  | 'primarySuccess'
  | 'primarySuccessDark'
  | 'primaryWarningDark'
  | 'secondary'
  | 'secondaryInverse'
  | 'secondaryWarning'
  | 'secondarySuccess'
  | 'tertiary'
  | 'tertiaryInverse'
  | 'tertiaryOutline'
  | 'ghost'
  | 'ghostInverse'
  | 'text';

type ButtonSize = 'sm' | 'md' | 'lg';

export type ButtonProps = {
  className?: string;
  size?: ButtonSize;
  trackMetadata?: Record<string, unknown>;
  trackName?: string;
  variant?: ButtonVariant;
};

export const Button = ({
  children,
  className,
  onClick,
  size = 'lg',
  trackMetadata,
  trackName,
  variant = 'primary',
  ...buttonProps
}: ButtonProps & React.ComponentPropsWithRef<'button'>) => {
  return (
    <button
      className={twMerge(
        clsx(
          'rounded-full font-medium transition-colors duration-300 focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-button-activeBorder',
          size === 'sm' && 'px-12 py-6 text-sm leading-gripped',
          size === 'md' && 'px-16 py-8 text-md leading-normal',
          size === 'lg' && 'px-24 py-12 text-md leading-normal',
          variant === 'primary' &&
            'bg-primary-400 text-white hover:bg-primary-500 disabled:bg-grey-75 disabled:text-grey-200',
          variant === 'primaryLight' &&
            'bg-primary-90 text-primary-400 hover:bg-primary-100 disabled:border-2 disabled:border-grey-200 disabled:bg-transparent disabled:text-grey-200',
          variant === 'primaryBright' &&
            'bg-primary-300 text-white hover:bg-primary-500 disabled:bg-white disabled:bg-opacity-20 disabled:text-opacity-20',
          variant === 'primaryInverse' &&
            'bg-secondary-75 text-grey-500 hover:bg-secondary-100 disabled:bg-grey-400 disabled:text-grey-300',
          variant === 'primarySuccess' &&
            'bg-success-350 text-white hover:bg-success-500 disabled:bg-grey-400 disabled:text-grey-300',
          variant === 'primarySuccessDark' &&
            'bg-success-550 text-white hover:bg-success-350 disabled:bg-grey-200 disabled:text-grey-75',
          variant === 'primaryWarningDark' &&
            'bg-warning-600 text-white hover:bg-warning-500 disabled:bg-white disabled:text-grey-200',
          variant === 'secondary' &&
            'border-2 border-primary-400 bg-transparent text-primary-400 hover:border-primary-400 hover:bg-primary-400 hover:text-white focus:border-primary-400 focus:bg-primary-400 focus:text-white disabled:border-grey-200 disabled:bg-transparent disabled:text-grey-200',
          variant === 'secondaryInverse' &&
            'border-2 border-secondary-75 bg-transparent text-secondary-75 hover:border-secondary-100 hover:bg-secondary-100 hover:text-grey-500 focus:border-secondary-75 focus:bg-secondary-75 focus:text-grey-500 disabled:border-grey-300 disabled:bg-transparent disabled:text-grey-300',
          variant === 'secondaryWarning' &&
            'border-2 border-warning-550 bg-transparent text-warning-550 hover:bg-warning-550 hover:text-warning-75 focus:bg-warning-550 focus:text-warning-75 active:bg-warning-550 active:text-warning-75 disabled:border-grey-300 disabled:bg-transparent disabled:text-grey-300',
          variant === 'secondarySuccess' &&
            'border-2 border-success-550 bg-transparent text-success-550 hover:bg-success-550 hover:text-success-50 focus:bg-success-550 focus:text-success-50 active:bg-success-550 active:text-success-50 disabled:border-grey-300 disabled:bg-transparent disabled:text-grey-300',
          variant === 'tertiary' &&
            'bg-grey-100 text-grey-500 hover:bg-grey-200 disabled:border-2 disabled:border-grey-200 disabled:bg-transparent disabled:text-grey-200',
          variant === 'tertiaryInverse' &&
            'bg-white text-grey-500 hover:bg-grey-75 disabled:bg-grey-400 disabled:text-grey-300',
          variant === 'tertiaryOutline' &&
            'border-2 border-white bg-transparent text-white hover:border-grey-75 hover:bg-grey-75 hover:text-grey-500 focus:border-grey-75 focus:bg-grey-75 focus:text-grey-500 active:bg-grey-75 active:text-grey-500 disabled:border-grey-400 disabled:bg-grey-400 disabled:text-grey-300',
          variant === 'ghost' &&
            'bg-transparent text-grey-500 hover:bg-primary-300 hover:bg-opacity-15 hover:text-primary-300 disabled:bg-transparent disabled:text-grey-200',
          variant === 'ghostInverse' &&
            'bg-transparent text-white hover:bg-white hover:bg-opacity-20 disabled:bg-transparent disabled:text-grey-300',
          variant === 'text' && 'bg-transparent px-0 py-0 text-inherit disabled:opacity-30',
          className,
        ),
      )}
      onClick={(e) => {
        trackName && trackEvent('Clicked', { name: trackName, ...trackMetadata });
        onClick && onClick(e);
      }}
      {...buttonProps}
    >
      {children}
    </button>
  );
};
